import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { PARTNERS } from "@/content/site";
import useMediaQuery from "@/hooks/useMediaQuery";
import useScrollProgress from "@/hooks/useScrollProgress";
import Reveal from "@/components/Reveal";

const WAVE_SIZE = 6;

const THREAD_GRAD = (id) => (
  <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
    <stop offset="0%" stopColor="#6E5A7B" />
    <stop offset="50%" stopColor="#E56B6F" />
    <stop offset="100%" stopColor="#EAAD8C" />
  </linearGradient>
);

/* ---------------- Desktop node (spark on the thread) ---------------- */
function DesktopNode({ partner, pos, count, above, local, reduced }) {
  const t = (pos + 0.6) / count;
  const a = [Math.max(0, t - 0.14), t];
  const opacity = useTransform(local, a, [0, 1], { clamp: true });
  const scale = useTransform(local, a, [0.92, 1], { clamp: true });
  const y = useTransform(local, a, [12, 0], { clamp: true });
  const dotScale = useTransform(local, a, [0, 1], { clamp: true });
  const sparkOpacity = useTransform(local, [t - 0.06, t, t + 0.1], [0, 1, 0], { clamp: true });

  const left = `${(pos + 0.5) / count * 100}%`;
  const staticStyle = reduced ? { opacity: 1, scale: 1, y: 0 } : { opacity, scale, y };
  const dotStatic = reduced ? { scale: 1 } : { scale: dotScale };

  return (
    <>
      {/* dot on the thread */}
      <motion.span
        aria-hidden="true"
        className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ left, top: "50%", width: 12, height: 12, backgroundColor: "var(--coral)", boxShadow: "0 0 0 5px rgba(229,107,111,0.18)", ...dotStatic }}
      />
      {/* spark flash */}
      {!reduced && (
        <motion.span aria-hidden="true" className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left, top: "50%", width: 30, height: 30, background: "radial-gradient(circle, rgba(234,173,140,0.9), transparent 70%)", opacity: sparkOpacity }} />
      )}
      {/* logo + name */}
      <motion.div
        role="listitem"
        aria-label={partner.name}
        data-partner-slug={partner.slug}
        data-testid={`partner-${partner.slug}`}
        className={`absolute -translate-x-1/2 flex flex-col items-center w-36 text-center ${above ? "-translate-y-full" : ""}`}
        style={{ left, top: above ? "calc(50% - 26px)" : "calc(50% + 26px)", ...staticStyle }}
      >
        {above && (
          <>
            <img src={partner.src} alt={`شعار ${partner.name}`} loading="lazy" data-testid={`partner-logo-${partner.slug}`} className="max-h-14 w-auto object-contain" style={{ filter: "drop-shadow(0 4px 10px rgba(40,62,85,0.10))" }} />
            <span data-testid={`partner-name-${partner.slug}`} className="mt-2 text-[0.72rem] leading-tight text-navy/70 line-clamp-2">{partner.name}</span>
          </>
        )}
        {!above && (
          <>
            <span data-testid={`partner-name-${partner.slug}`} className="mb-2 text-[0.72rem] leading-tight text-navy/70 line-clamp-2">{partner.name}</span>
            <img src={partner.src} alt={`شعار ${partner.name}`} loading="lazy" data-testid={`partner-logo-${partner.slug}`} className="max-h-14 w-auto object-contain" style={{ filter: "drop-shadow(0 4px 10px rgba(40,62,85,0.10))" }} />
          </>
        )}
      </motion.div>
    </>
  );
}

function DesktopWave({ items, startIndex, reduced }) {
  const waveRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: waveRef, offset: ["start 0.9", "start 0.35"] });
  const gid = `pw-${startIndex}`;
  const local = scrollYProgress;
  const pathLength = reduced ? 1 : local;
  const n = items.length;

  return (
    <div ref={waveRef} className="relative" dir="ltr" style={{ height: 240 }}>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 240" fill="none" preserveAspectRatio="none" aria-hidden="true">
        <defs>{THREAD_GRAD(gid)}</defs>
        <path d="M0,120 C160,52 320,188 500,120 C680,52 840,188 1000,120" stroke="rgba(110,90,123,0.12)" strokeWidth="1.5" />
        <motion.path d="M0,120 C160,52 320,188 500,120 C680,52 840,188 1000,120" stroke={`url(#${gid})`} strokeWidth="2" strokeLinecap="round" style={{ pathLength }} />
      </svg>
      {items.map((p, j) => (
        <DesktopNode key={p.src} partner={p} pos={j} count={n} above={j % 2 === 0} local={local} reduced={reduced} />
      ))}
    </div>
  );
}

/* ---------------- Mobile: serpentine spark grid (compact) ---------------- */
function MobilePartners({ reduced }) {
  const ref = useRef(null);
  const p = useScrollProgress(ref);
  const eff = reduced ? 1 : p;
  const n = PARTNERS.length;
  const cols = 3;
  const rows = Math.ceil(n / cols);

  // serpentine visit order for each grid index + the connecting path
  const { order, d } = useMemo(() => {
    const m = new Array(n);
    const pts = [];
    let k = 0;
    for (let r = 0; r < rows; r++) {
      const cs = [];
      for (let c = 0; c < cols; c++) cs.push(c);
      if (r % 2 === 1) cs.reverse();
      for (const c of cs) {
        const idx = r * cols + c;
        if (idx < n) { m[idx] = k++; pts.push([c * 100 + 50, r * 100 + 50]); }
      }
    }
    const path = pts.map((pt, i) => (i === 0 ? `M${pt[0]},${pt[1]}` : `L${pt[0]},${pt[1]}`)).join(" ");
    return { order: m, d: path };
  }, [n, rows]);

  return (
    <div ref={ref} className="relative mt-12">
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${cols * 100} ${rows * 100}`} preserveAspectRatio="none" fill="none" aria-hidden="true">
        <defs>{THREAD_GRAD("pw-m")}</defs>
        <path d={d} stroke="rgba(110,90,123,0.14)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
        <path d={d} stroke="url(#pw-m)" strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" pathLength="1" style={{ strokeDasharray: 1, strokeDashoffset: 1 - eff }} />
      </svg>
      <ul role="list" aria-label="شركاؤنا وعملاؤنا" className="relative grid grid-cols-3 gap-x-2 gap-y-1" style={{ gridAutoRows: "112px" }}>
        {PARTNERS.map((partner, idx) => {
          const t = (order[idx] + 0.6) / n;
          const rv = reduced ? 1 : Math.max(0, Math.min(1, (eff - (t - 0.12)) / 0.12));
          return (
            <li key={partner.src} className="relative flex flex-col items-center justify-center text-center px-1" data-partner-slug={partner.slug} data-testid={`partner-${partner.slug}`}
              style={{ opacity: rv, transform: reduced ? "none" : `translateY(${(1 - rv) * 10}px) scale(${0.9 + 0.1 * rv})` }}>
              <span aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                style={{ width: 46, height: 46, background: "radial-gradient(circle, rgba(234,173,140,0.5), transparent 70%)", opacity: rv }} />
              <img src={partner.src} alt={`شعار ${partner.name}`} loading="lazy" data-testid={`partner-logo-${partner.slug}`} className="relative max-h-9 w-auto object-contain" />
              <span data-testid={`partner-name-${partner.slug}`} className="relative mt-1.5 text-[0.62rem] leading-tight text-navy/70 line-clamp-2">{partner.name}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Partners() {
  const reduced = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const waves = [];
  for (let i = 0; i < PARTNERS.length; i += WAVE_SIZE) waves.push(PARTNERS.slice(i, i + WAVE_SIZE));

  return (
    <section data-testid="partners-section" className="border-t border-navy/10" style={{ background: "linear-gradient(180deg, #FBF4EF 0%, #FDFDFD 100%)" }}>
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-28">
        <Reveal>
          <span className="eyebrow mb-5">شركاؤنا</span>
          <h2 className="font-display text-navy leading-tight max-w-2xl" style={{ fontSize: "clamp(1.5rem, 2.9vw, 2.4rem)" }}>
            كل شراكة هي عبارة عن قصة فريدة بدأت من سياق الاحتياج وامتدت لسياق الأثر.
          </h2>
        </Reveal>

        {isDesktop ? (
          <div className="mt-16 space-y-2" role="list" aria-label="شركاؤنا وعملاؤنا">
            {waves.map((items, wi) => (
              <DesktopWave key={wi} items={items} startIndex={wi * WAVE_SIZE} reduced={reduced} />
            ))}
          </div>
        ) : (
          <MobilePartners reduced={reduced} />
        )}
      </div>
    </section>
  );
}
